<template>
  <div>
    <template v-if="editionMode">
      <div
        class="grid gap-3"
        :class="priceDisplayMode === 'all' ? 'grid-cols-3' : 'grid-cols-1'"
      >
        <div
          v-if="showHtva"
          class="relative"
          :class="
            priceEncodingType === PriceEncodingTypes.HTVA
              ? 'order-1'
              : 'order-3'
          "
        >
          <span
            v-if="
              priceEncodingType === PriceEncodingTypes.HTVA && displayLockButton
            "
            class="absolute bottom-2 left-3 z-10 flex flex-row items-center"
          >
            <button
              @click="
                canLockUnlock
                  ? (localLocked = !locked)
                  : $h.toastInfo($t('toasts.price_locked'))
              "
            >
              <FontAwesomeIcon :icon="['fas', locked ? 'lock' : 'lock-open']" />
            </button>
          </span>
          <BaseShowEditPriceInput
            v-model="localHtvaValue"
            :label="
              priceEncodingType === PriceEncodingTypes.HTVA
                ? `${label} (${$t('global.excluding_vat')})`
                : $t('global.excluding_vat')
            "
            :edition-mode="editionMode"
            :required="
              required && priceEncodingType === PriceEncodingTypes.HTVA
            "
            price-type="htva"
            :disabled="
              (!isSingleDisplayMode &&
                priceEncodingType === PriceEncodingTypes.TVAC) ||
              locked ||
              disabled
            "
            :currency="defaultCurrency"
            :errors="errorsPriceHtva"
          />
        </div>
        <div v-if="showTva" class="order-2">
          <BaseShowEditPriceInput
            v-model="localTvaValue"
            :label="`${$t('global.vat')} ${tvaRateAsLabel}`"
            :edition-mode="editionMode"
            :disabled="true"
            price-type="tva"
            :currency="defaultCurrency"
            :errors="errorsPriceTvaValue"
          />
        </div>
        <div
          v-if="showTvac"
          class="relative"
          :class="
            priceEncodingType === PriceEncodingTypes.TVAC
              ? 'order-1'
              : 'order-3'
          "
        >
          <span
            v-if="
              priceEncodingType === PriceEncodingTypes.TVAC && displayLockButton
            "
            class="absolute bottom-2 left-3 z-10 flex flex-row items-center"
          >
            <button
              @click="
                canLockUnlock
                  ? (localLocked = !locked)
                  : $h.toastInfo($t('toasts.price_locked'))
              "
            >
              <FontAwesomeIcon :icon="['fas', locked ? 'lock' : 'lock-open']" />
            </button>
          </span>
          <BaseShowEditPriceInput
            v-model="localTvacValue"
            :label="
              priceEncodingType === PriceEncodingTypes.TVAC
                ? `${label} (${$t('global.tax_included')})`
                : $t('global.tax_included')
            "
            :required="
              required && priceEncodingType === PriceEncodingTypes.TVAC
            "
            :edition-mode="editionMode"
            price-type="tvac"
            :disabled="
              (!isSingleDisplayMode &&
                priceEncodingType === PriceEncodingTypes.HTVA) ||
              locked ||
              disabled
            "
            :currency="defaultCurrency"
            :errors="errorsPriceTvac"
          />
        </div>
      </div>
      <ErrorContainer :error-key="name" :errors="errors" />
    </template>
    <template v-else>
      <div
        class="grid gap-3"
        :class="priceDisplayMode === 'all' ? 'grid-cols-3' : 'grid-cols-1'"
      >
        <div
          v-if="showHtva"
          :class="
            priceEncodingType === PriceEncodingTypes.HTVA
              ? 'order-1'
              : 'order-3'
          "
        >
          <BaseShowPriceLabel
            :label="
              priceEncodingType === PriceEncodingTypes.HTVA
                ? `${label} (${$t('global.excluding_vat')})`
                : $t('global.excluding_vat')
            "
            :value="localHtvaValue"
            price-type="excluding_vat"
            :currency="defaultCurrency"
          />
        </div>
        <div v-if="showTva" class="order-2">
          <BaseShowPriceLabel
            :value="localTvaValue"
            price-type="vat"
            :label="`${$t('global.vat')} ${tvaRateAsLabel}`"
            :currency="defaultCurrency"
          />
        </div>
        <div
          v-if="showTvac"
          :class="
            priceEncodingType === PriceEncodingTypes.TVAC
              ? 'order-1'
              : 'order-3'
          "
        >
          <BaseShowPriceLabel
            :label="
              priceEncodingType === PriceEncodingTypes.TVAC
                ? `${label} (${$t('global.tax_included')})`
                : $t('global.tax_included')
            "
            :value="localTvacValue"
            price-type="tax_included"
            :currency="defaultCurrency"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import BaseShowEditPriceInput from '@c/addf-package/components/BaseShowEditInput/BaseShowEditPriceInput'
import BaseShowPriceLabel from '@c/addf-package/components/BaseLabel/BaseShowPriceLabel.vue'
import { PriceEncodingTypes } from '@/assets/enums'
import ErrorContainer from '@c/addf-package/components/BaseShowEditInput/ErrorContainer.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { mapGetters } from 'vuex'

export default {
  name: 'BaseShowEditPricingInput',
  components: {
    FontAwesomeIcon,
    ErrorContainer,
    BaseShowPriceLabel,
    BaseShowEditPriceInput
  },
  props: {
    vatRate: {
      type: [String, Number],
      required: true
    },
    displayTvaRateAsLabel: {
      type: Boolean,
      required: false,
      default: true
    },
    label: {
      type: String,
      required: false,
      default: null
    },
    errors: {
      type: [Object, Array],
      required: false,
      default: null
    },
    errorsPriceHtva: {
      type: Array,
      default() {
        return []
      }
    },
    errorsPriceTvac: {
      type: Array,
      default() {
        return []
      }
    },
    errorsPriceTvaValue: {
      type: Array,
      default() {
        return []
      }
    },
    editionMode: {
      type: Boolean,
      required: true
    },
    htvaValue: {
      type: Number,
      required: false,
      default: null
    },
    tvacValue: {
      type: Number,
      required: false,
      default: null
    },
    tvaValue: {
      type: Number,
      required: false,
      default: null
    },
    isVertical: {
      type: Boolean,
      required: false,
      default: false
    },
    required: {
      type: Boolean,
      required: false,
      default: false
    },
    displayLockButton: {
      type: Boolean,
      required: false,
      default: false
    },
    canLockUnlock: {
      type: Boolean,
      required: false,
      default: false
    },
    locked: {
      type: Boolean,
      required: false,
      default: false
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    countDecimal: {
      type: Number,
      required: false,
      default: 2
    },
    pricingBasis: {
      type: String,
      required: false,
      default: null
    },
    priceDisplayMode: {
      type: String,
      required: false,
      default: 'all',
      validator: (v) => ['all', 'htva', 'tvac'].includes(v)
    },
    currency: {
      type: String,
      required: false,
      default: null
    }
  },
  emits: [
    'update:htvaValue',
    'update:tvacValue',
    'update:tvaValue',
    'update:locked'
  ],
  computed: {
    ...mapGetters({
      initialMetadata: 'metadata/getInitialMetadata',
      currencySettings: 'metadata/getDefaultCurrencyMetadata'
    }),
    defaultCurrency() {
      return this.currency ?? this.currencySettings
    },
    showHtva() {
      return this.priceDisplayMode === 'all' || this.priceDisplayMode === 'htva'
    },
    showTvac() {
      return this.priceDisplayMode === 'all' || this.priceDisplayMode === 'tvac'
    },
    showTva() {
      return this.priceDisplayMode === 'all'
    },
    isSingleDisplayMode() {
      return this.priceDisplayMode !== 'all'
    },
    priceEncodingType() {
      if (this.priceDisplayMode !== 'all')
        return this.priceDisplayMode.toLocaleUpperCase()
      if (!this.pricingBasis)
        return this.initialMetadata?.settings?.price_encoding_type
      return this.pricingBasis
    },
    PriceEncodingTypes() {
      return PriceEncodingTypes
    },
    tvaRateAsLabel() {
      return '(' + this.safeVatRate.toString().replace('.', ',') + '%)'
    },
    // Un article catalogue sans prix/TVA configuré renvoie une valeur non
    // numérique (NaN) ici : sans ce filet, elle se propageait telle quelle
    // dans les calculs ci-dessous (value * (1 + NaN / 100) = NaN partout),
    // et NaN n'étant jamais égal à lui-même, le watcher vatRate ci-dessous
    // pouvait se redéclencher en boucle à chaque cycle réactif.
    safeVatRate() {
      const rate = parseFloat(this.vatRate)
      return Number.isFinite(rate) ? rate : 0
    },
    localHtvaValue: {
      get() {
        return this.htvaValue
      },
      set(value) {
        this.updateFromHTVA(value)
        this.$emit('update:htvaValue', this.fixDecimalValue(value))
      }
    },
    localTvacValue: {
      get() {
        return this.tvacValue
      },
      set(value) {
        this.updateFromTVAC(value)
        this.$emit('update:tvacValue', this.fixDecimalValue(value))
      }
    },
    localTvaValue: {
      get() {
        return this.tvaValue
      },
      set(value) {
        this.$emit('update:tvaValue', this.fixDecimalValue(value))
      }
    },
    localLocked: {
      get() {
        return this.locked
      },
      set(value) {
        this.$emit('update:locked', value)
      }
    }
  },
  watch: {
    vatRate(newValue, oldValue) {
      // Object.is (pas !==) : NaN !== NaN vaut toujours true, ce qui
      // redéclencherait ce watcher indéfiniment si vatRate reste NaN d'un
      // cycle réactif à l'autre.
      if (!Object.is(newValue, oldValue)) {
        this.updateFromTauxTVA(newValue)
      }
    }
  },
  mounted() {
    if (typeof this.localHtvaValue === 'string') {
      this.localHtvaValue = parseFloat(this.localHtvaValue)
    }

    if (typeof this.localTvacValue === 'string') {
      this.localTvacValue = parseFloat(this.localTvacValue)
    }

    if (typeof this.localTvaValue === 'string') {
      this.localTvaValue = parseFloat(this.localTvaValue)
    }

    // if (typeof this.localLocked === 'string') {
    //   this.localLocked = parseFloat(this.localLocked)
    // }
  },
  methods: {
    updateFromTVAC(value) {
      if (this.priceEncodingType !== 'TVAC') {
        return
      }
      let htvaValue = value / (1 + this.safeVatRate / 100)

      this.localHtvaValue = htvaValue
      this.localTvaValue = value - htvaValue
    },
    updateFromHTVA(value) {
      if (this.priceEncodingType !== 'HTVA') {
        return
      }
      let tvacValue = value * (1 + this.safeVatRate / 100)
      this.localTvacValue = tvacValue
      this.localTvaValue = tvacValue - value
    },
    updateFromTauxTVA(value) {
      const rate = Number.isFinite(parseFloat(value)) ? parseFloat(value) : 0
      if (this.priceEncodingType === 'TVAC') {
        let htvaValue = this.localTvacValue / (1 + rate / 100)
        this.localHtvaValue = htvaValue
        this.localTvaValue = this.localTvacValue - htvaValue
      } else {
        let tvacValue = this.localHtvaValue * (1 + rate / 100)
        this.localTvacValue = tvacValue
        this.localTvaValue = tvacValue - this.localHtvaValue
      }
    },
    fixDecimalValue(value) {
      return parseFloat(parseFloat(value).toFixed(this.countDecimal))
    }
  }
}
</script>
